import { useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { notifyError, notify } from "../../../utils/toast";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";

// ─────────────────────────────────────────────────────────────
// GraphQL
// ─────────────────────────────────────────────────────────────

const ADMIN_CURRENT_MONTH_PAYOUT_PREVIEW = gql`
  query AdminCurrentMonthPayoutPreview {
    adminCurrentMonthPayoutPreview {
      sellerId
      sellerName
      storeName
      storeId
      tossSellerStatus
      totalAmount
      orderCount
      paidAmount
      unpaidAmount
    }
  }
`;

const ADMIN_EXECUTE_EXCEPTIONAL_SELLER_PAYOUTS = gql`
  mutation AdminExecuteExceptionalSellerPayouts($sellerIds: [Int!]!) {
    adminExecuteExceptionalSellerPayouts(sellerIds: $sellerIds) {
      ok
      successCount
      failedCount
      skippedCount
      totalAmount
      message
    }
  }
`;

// ─────────────────────────────────────────────────────────────
// 타입
// ─────────────────────────────────────────────────────────────

interface CurrentMonthPreviewRow {
  sellerId: number;
  sellerName: string | null;
  storeName: string | null;
  storeId: number | null;
  tossSellerStatus: string | null;
  totalAmount: number;
  orderCount: number;
  paidAmount: number;
  unpaidAmount: number;
}

interface ExecuteResult {
  ok: boolean;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  totalAmount: number;
  message: string | null;
}

// ─────────────────────────────────────────────────────────────
// 유틸
// ─────────────────────────────────────────────────────────────

function formatKRW(v: number) {
  return v.toLocaleString("ko-KR") + "원";
}

function getCurrentYearMonth(): string {
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  const y = kst.getUTCFullYear();
  const m = kst.getUTCMonth() + 1;
  return `${y}년 ${m}월`;
}

const SELLER_STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  APPROVED: { label: "완료 ✅", cls: "bg-green-100 text-green-700" },
  PARTIALLY_APPROVED: { label: "완료 ✅", cls: "bg-green-100 text-green-700" },
  APPROVAL_REQUIRED: { label: "미완료 ⚠️", cls: "bg-yellow-100 text-yellow-700" },
  KYC_REQUIRED: { label: "미완료 ⚠️", cls: "bg-yellow-100 text-yellow-700" },
};

function SellerStatusBadge({ status }: { status: string | null }) {
  if (!status)
    return <span className="text-red-500 text-xs font-medium">미등록 ❌</span>;
  const info = SELLER_STATUS_LABEL[status] ?? {
    label: status,
    cls: "bg-gray-100 text-gray-600",
  };
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${info.cls}`}>
      {info.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// 컴포넌트
// ─────────────────────────────────────────────────────────────

export default function PayoutPreview() {
  const currentYearMonth = getCurrentYearMonth();

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [lastResult, setLastResult] = useState<ExecuteResult | null>(null);

  const { data, loading, refetch } = useQuery(ADMIN_CURRENT_MONTH_PAYOUT_PREVIEW, {
    fetchPolicy: "network-only",
  });

  const [executeExceptional, { loading: executeLoading }] = useMutation(
    ADMIN_EXECUTE_EXCEPTIONAL_SELLER_PAYOUTS,
  );

  const rows: CurrentMonthPreviewRow[] = data?.adminCurrentMonthPayoutPreview ?? [];

  const totalAmount = rows.reduce((s, r) => s + r.totalAmount, 0);
  const totalPaid = rows.reduce((s, r) => s + r.paidAmount, 0);
  const totalUnpaid = rows.reduce((s, r) => s + r.unpaidAmount, 0);
  const totalOrders = rows.reduce((s, r) => s + r.orderCount, 0);

  const selectedRows = rows.filter((r) => selectedIds.has(r.sellerId));
  const selectedTotal = selectedRows.reduce((s, r) => s + r.totalAmount, 0);

  const toggleRow = (sellerId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sellerId)) next.delete(sellerId);
      else next.add(sellerId);
      return next;
    });
  };

  const handleExecute = async () => {
    if (selectedIds.size === 0) return;

    const confirmMsg = `선택한 ${selectedIds.size}명 / 총 ${formatKRW(selectedTotal)}을 이번달 포함하여 예외 지급하시겠습니까?\n\n이 작업은 당월 주문까지 포함하여 실제 송금됩니다.`;
    if (!window.confirm(confirmMsg)) return;

    try {
      const res = await executeExceptional({
        variables: { sellerIds: Array.from(selectedIds) },
      });
      const data = res.data?.adminExecuteExceptionalSellerPayouts as ExecuteResult | undefined;
      if (data?.ok) {
        setLastResult(data);
        setSelectedIds(new Set());
        notify(data.message ?? "예외 지급 실행 완료");
        await refetch();
      } else {
        notifyError("예외 지급 실행에 실패했습니다.");
      }
    } catch (e) {
      notifyError(e instanceof Error ? e.message : "예외 지급 실행 오류");
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-1 flex items-center gap-3">
        <h1 className="text-2xl font-bold">이번달 정산 미리보기</h1>
        <span className="rounded-full bg-blue-100 px-3 py-0.5 text-sm font-semibold text-blue-700">
          {currentYearMonth}
        </span>
      </div>
      <p className="mb-8 text-sm text-gray-500">
        {currentYearMonth}에 발생한 확정 주문을 셀러별로 집계합니다. 체크박스로 셀러를 선택하면
        당월 주문을 포함한 예외 지급을 실행할 수 있습니다.
      </p>

      {/* 요약 카드 */}
      <div className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "셀러 수", value: `${rows.length}명`, cls: "text-gray-900" },
          { label: "총 주문", value: `${totalOrders}건`, cls: "text-gray-900" },
          { label: "이번달 총액", value: formatKRW(totalAmount), cls: "text-indigo-700" },
          {
            label: "미지급",
            value: formatKRW(totalUnpaid),
            sub: `지급완료 ${formatKRW(totalPaid)}`,
            cls: "text-blue-700",
          },
        ].map((card) => (
          <div
            key={card.label}
            className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm"
          >
            <p className="text-xs font-medium text-gray-500 mb-0.5">{card.label}</p>
            <p className={`text-lg font-bold ${card.cls}`}>{card.value}</p>
            {"sub" in card && card.sub && (
              <p className="text-xs text-gray-400 mt-0.5">{card.sub}</p>
            )}
          </div>
        ))}
      </div>

      {/* 예외 지급 실행 영역 */}
      <div className="mb-6 rounded-xl border border-orange-200 bg-orange-50 px-5 py-4 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm font-semibold text-orange-800">예외 지급 실행</p>
            <p className="text-xs text-orange-600 mt-0.5">
              테이블에서 셀러를 선택하면 당월 주문까지 포함하여 즉시 지급합니다
            </p>
          </div>
          <div className="flex items-center gap-3">
            {selectedIds.size > 0 && (
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-orange-500 hover:text-orange-700 underline"
              >
                선택 해제
              </button>
            )}
            <button
              onClick={handleExecute}
              disabled={selectedIds.size === 0 || executeLoading}
              className="rounded-lg bg-orange-500 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {executeLoading
                ? "지급 실행 중..."
                : selectedIds.size > 0
                  ? `선택 ${selectedIds.size}명 예외 지급 (${formatKRW(selectedTotal)})`
                  : "셀러를 선택하세요"}
            </button>
          </div>
        </div>

        {lastResult && (
          <div className="mt-4 rounded-lg border border-orange-100 overflow-hidden bg-white">
            <div className="flex items-center justify-between bg-gray-50 px-4 py-2 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-600">예외 지급 결과</p>
              <button
                onClick={() => setLastResult(null)}
                className="text-xs text-gray-300 hover:text-gray-500"
              >
                닫기
              </button>
            </div>
            <div className="grid grid-cols-4 divide-x divide-gray-100">
              {[
                { label: "성공", value: `${lastResult.successCount}명`, cls: "text-green-600" },
                { label: "총 지급액", value: formatKRW(lastResult.totalAmount), cls: "text-indigo-600" },
                { label: "실패", value: `${lastResult.failedCount}명`, cls: "text-red-500" },
                { label: "스킵(미등록)", value: `${lastResult.skippedCount}명`, cls: "text-yellow-600" },
              ].map((item) => (
                <div key={item.label} className="px-4 py-3 text-center">
                  <p className="text-xs text-gray-400 mb-0.5">{item.label}</p>
                  <p className={`text-base font-bold ${item.cls}`}>{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 테이블 */}
      <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div>
            <h2 className="text-sm font-semibold text-gray-700">셀러별 집계</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              확정 주문 기준 · 취소 제외 · 총액 내림차순
              {selectedIds.size > 0 && (
                <span className="ml-2 text-orange-500 font-medium">
                  {selectedIds.size}명 선택됨
                </span>
              )}
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            새로고침
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinnerInline />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="pl-4 pr-2 py-3 w-10" />
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">매장명</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">셀러 등록</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">주문수</th>
                  <th className="px-4 py-3 text-right font-semibold text-blue-600">미지급</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-400">지급완료</th>
                  <th className="px-4 py-3 text-right font-semibold text-indigo-600">총액</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-10 text-center text-sm text-gray-400">
                      {currentYearMonth} 확정 주문이 없습니다.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const isChecked = selectedIds.has(row.sellerId);
                    return (
                      <tr
                        key={row.sellerId}
                        onClick={() => toggleRow(row.sellerId)}
                        className={`cursor-pointer transition-colors hover:bg-orange-50/40 ${isChecked ? "bg-orange-50" : ""}`}
                      >
                        <td className="pl-4 pr-2 py-3">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleRow(row.sellerId)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 rounded cursor-pointer accent-orange-500"
                          />
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-800">
                          {row.storeName ?? row.sellerName ?? `셀러 #${row.sellerId}`}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <SellerStatusBadge status={row.tossSellerStatus} />
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600">{row.orderCount}건</td>
                        <td className="px-4 py-3 text-right font-medium text-blue-700">
                          {row.unpaidAmount > 0 ? (
                            formatKRW(row.unpaidAmount)
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-400">
                          {row.paidAmount > 0 ? (
                            formatKRW(row.paidAmount)
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-indigo-700">
                          {formatKRW(row.totalAmount)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-gray-300 bg-gray-100">
                    <td />
                    <td className="px-4 py-3 text-sm font-bold text-gray-700">
                      합계 {rows.length}명
                    </td>
                    <td />
                    <td className="px-4 py-3 text-center font-bold text-gray-700">
                      {totalOrders}건
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-blue-700">
                      {formatKRW(totalUnpaid)}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-gray-400">
                      {formatKRW(totalPaid)}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-indigo-700">
                      {formatKRW(totalAmount)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
